import { useState, useEffect, useCallback } from 'react';
import { NotificationSettings, NotificationSettingKey, NotificationSettingModel } from '../types/notifications';
import { notificationService } from '../services/notificationService';

export function useNotificationSettings() {
  const [settings, setSettings] = useState<NotificationSettings>(() =>
    notificationService.getNotificationSettings()
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load settings from backend on mount
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        const loadedSettings = await notificationService.loadNotificationSettings();
        setSettings(loadedSettings);
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load notification settings';
        setError(errorMessage);
        console.error('Failed to load notification settings:', err);
        // Use local storage fallback
        const localSettings = notificationService.getNotificationSettings();
        setSettings(localSettings);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Update a single notification setting
  const updateSetting = useCallback(async (key: NotificationSettingKey, enabled: boolean) => {
    setIsLoading(true);
    setError(null);

    try {
      const settingModel: NotificationSettingModel = {
        setting: key,
        enabled,
      };

      await notificationService.updateNotificationSetting(settingModel);
      
      // Update local state
      setSettings(prev => ({
        ...prev,
        [key]: enabled,
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update notification setting';
      setError(errorMessage);
      console.error('Failed to update notification setting:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update multiple settings
  const updateSettings = useCallback(async (newSettings: Partial<NotificationSettings>) => {
    setIsLoading(true);
    setError(null);

    try {
      await notificationService.updateNotificationSettings(newSettings);
      
      // Update local state
      setSettings(prev => ({
        ...prev,
        ...newSettings,
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update notification settings';
      setError(errorMessage);
      console.error('Failed to update notification settings:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Reset to default settings
  const resetSettings = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await notificationService.resetNotificationSettings();
      const defaultSettings = notificationService.getNotificationSettings();
      setSettings(defaultSettings);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset notification settings';
      setError(errorMessage);
      console.error('Failed to reset notification settings:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Request browser notification permission
  const requestPermission = useCallback(async () => {
    setIsLoading(true);
    try {
      return await notificationService.requestNotificationPermission();
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Show a test notification
  const showTestNotification = useCallback(() => {
    return notificationService.showNotification(
      'Chime Notification Test', 
      {
        body: 'Your notification settings are working!',
        tag: 'test',
      }
    );
  }, []);

  return {
    settings,
    isLoading,
    error,
    updateSetting,
    updateSettings,
    resetSettings,
    requestPermission,
    showTestNotification,
  };
}